import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';

function Login() {
  const navigate = useNavigate();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const endpoint = isSignUp ? '/api/auth/register/' : '/api/auth/login/';
      const body = isSignUp
        ? { email, username: email, password, password_confirm: password }
        : { username: email, password };

      const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (response.ok) {
        // Store the tokens (SimpleJWT returns 'access' and 'refresh')
        const token = data.access || data.token;
        const refreshToken = data.refresh;

        localStorage.setItem('accessToken', token);
        if (refreshToken) {
          localStorage.setItem('refreshToken', refreshToken);
        }
        if (data.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
        }
        navigate('/account');
      } else {
        setError(data.error || data.detail || 'Authentication failed');
      }
    } catch (err) {
      setError('Network error. Please check if the backend is running.');
      console.error('Auth error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/google/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_token: credentialResponse.credential,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Store the tokens
        localStorage.setItem('accessToken', data.access);
        if (data.refresh) {
          localStorage.setItem('refreshToken', data.refresh);
        }
        if (data.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
        }
        navigate('/account');
      } else {
        setError(data.error || 'Google Sign-In failed');
      }
    } catch (err) {
      setError('Network error. Please check if the backend is running.');
      console.error('Google Sign-In error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google Sign-In was cancelled or failed');
  };

  return (
    <div className="py-20 px-6 bg-background min-h-screen flex items-center justify-center">
      <div className="max-w-md w-full">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-3 mb-6">
            <div className="bg-primary rounded-lg p-3 shadow-lg">
              <i className="fas fa-shield-check text-secondary text-4xl"></i>
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-primary mb-4">
            {isSignUp ? 'Create Account' : 'Welcome Back'}
          </h1>
          <p className="text-slate-600">
            {isSignUp ? 'Sign up to get started with Scope Order' : 'Sign in to manage your Scope Order account'}
          </p>
        </div>

        <div className="bg-white p-10 rounded-2xl shadow-xl border border-slate-200">
          {/* Google Sign-In */}
          <div className="mb-6 flex justify-center">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              useOneTap
              text={isSignUp ? 'signup_with' : 'signin_with'}
              shape="rectangular"
              size="large"
            />
          </div>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-slate-500 font-medium">Or continue with email</span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleEmailAuth} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-bold text-slate-700 mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-bold text-slate-700 mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                placeholder="••••••••"
              />
              {isSignUp && (
                <p className="text-xs text-slate-500 mt-1">Minimum 6 characters</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-secondary text-white px-6 py-4 rounded-xl font-bold hover:bg-orange-600 transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : isSignUp ? 'Create Account' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsSignUp(!isSignUp);
                setError('');
              }}
              className="text-sm text-slate-600 hover:text-primary transition"
            >
              {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
            </button>
          </div>

          <div className="mt-8 text-center text-sm text-slate-500">
            By continuing, you agree to our{' '}
            <a href="/terms" className="text-secondary hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href="/privacy" className="text-secondary hover:underline">Privacy Policy</a>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-slate-600 mb-4">Don't have the app yet?</p>
          <div className="flex items-center justify-center space-x-4">
            <a href="https://apps.apple.com" className="flex items-center space-x-2 bg-primary text-white px-6 py-3 rounded-xl font-bold hover:bg-primary/90 transition">
              <i className="fab fa-apple text-xl"></i>
              <span>App Store</span>
            </a>
            <a href="https://play.google.com" className="flex items-center space-x-2 bg-primary text-white px-6 py-3 rounded-xl font-bold hover:bg-primary/90 transition">
              <i className="fab fa-google-play text-xl"></i>
              <span>Google Play</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
